import { useVerifyActionOnFlags } from '@/hooks/useVerifyActionOnFlags';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Alert, Box, Button, Icon, toast } from '@palmetto/palmetto-components';
import { FormikErrors, FormikValues, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Flag, MilestonePackageRoute, MilestoneRouteToQueueTypeMap, QueueStatus, QueueType } from 'types';

import { ReviewPackageActions } from '../../types/ReviewPackage';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { QueueFlagsContext } from '../QueueFlags/QueueFlagsContext';
import { CompleteQueueReview } from '../Queues/CompleteQueueReview/CompleteQueueReview';

interface FooterProps {
  isFirstSubmission: boolean;
  isLoading: boolean;
  canSubmitPackage: boolean;
  canSubmitReview: boolean;
  disableApproval?: boolean;
  disableAllApprovals?: boolean;
  hasPendingChangeOrder?: boolean;
  disabled?: boolean;
  onSave: ({
    accountId,
    data,
    targetStatus,
  }: {
    accountId: string;
    data: FormikValues;
    targetStatus?: QueueStatus;
  }) => Promise<void>;
  conditionallyApprovedFlags?: Flag[];
  subQueueInfo?: {
    type: QueueType | undefined;
    status: QueueStatus | undefined;
  };
}
export const Footer = ({
  isFirstSubmission,
  isLoading,
  canSubmitPackage,
  canSubmitReview,
  disableApproval,
  disableAllApprovals,
  hasPendingChangeOrder,
  onSave,
  disabled,
  conditionallyApprovedFlags,
  subQueueInfo,
}: FooterProps) => {
  const navigate = useNavigate();
  const { flagDrivenPackageReview = false } = useFlags();
  const { getAllFlags, getVisibleFlags } = useContext(QueueFlagsContext);
  const { id = '', action = ReviewPackageActions.review } = useParams<{
    id: string;
    action: ReviewPackageActions;
  }>();
  const { isSubmitting, errors, values, setSubmitting, touched } = useFormikContext<any>();
  const location = useLocation();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const milestonePackageURI = location.pathname.split('/')[3];

  const { data: queue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: MilestoneRouteToQueueTypeMap[milestonePackageURI as MilestonePackageRoute],
  });

  const showFlagsCountToEPC = flagDrivenPackageReview
    ? !(queue?.status === QueueStatus.submitted && !isFinCoUser)
    : true;

  const allowsAction = useIsOrgReadOnly();
  const flags = getAllFlags(false);
  const visibleFlags = getVisibleFlags(flags);
  const flagCount =
    visibleFlags.filter((flag) => (flagDrivenPackageReview ? flag?.flagType || flag?.dataKey : !flag?.flagType))
      .length + (conditionallyApprovedFlags?.length ?? 0);
  // A pending change order hard-blocks approval regardless of flagDrivenPackageReview: the CO
  // stipulation that drives this only exists when the LD flag is on, so it already gates itself.
  const shouldDisableReviewAsApproval =
    (flagDrivenPackageReview && (disableAllApprovals || disableApproval)) || Boolean(hasPendingChangeOrder);
  const { allRejectionFlagsHaveActionTaken } = useVerifyActionOnFlags(visibleFlags);

  const pureSave = async (targetStatus?: QueueStatus) => {
    await onSave({ accountId: id, data: values, targetStatus });
  };

  // Formik's isSubmitting only blocks the button after a re-render, so a fast double click
  // sends two concurrent saves that race each other on the backend.
  const isSaveInFlight = useRef(false);

  const handleSave = async (targetStatus?: QueueStatus) => {
    if (isSaveInFlight.current) return;
    isSaveInFlight.current = true;
    setSubmitting(true);
    try {
      await pureSave(targetStatus);
    } catch (e: any) {
      toast.error(e?.message || 'Error saving the package.');
    } finally {
      isSaveInFlight.current = false;
      setSubmitting(false);
    }
  };

  const formErrors = Object.keys(errors).reduce((acc: FormikErrors<any>, key: string) => {
    const errorVal = touched[key] ? { [key]: errors[key] } : {};
    return {
      ...acc,
      ...errorVal,
    };
  }, {});

  const isSubmitDisabled =
    !canSubmitPackage ||
    !!Object.keys(formErrors).length ||
    disabled ||
    isSubmitting ||
    !allowsAction ||
    !allRejectionFlagsHaveActionTaken;

  return (
    <Box direction="column" gap="2xs">
      {Object.keys(formErrors).length ? (
        <Box color="danger" fontSize="sm" padding="sm" style={{ marginLeft: 'auto' }}>
          This form contains errors. Please resolve before submitting.
        </Box>
      ) : null}

      {hasPendingChangeOrder && isFinCoUser && action === ReviewPackageActions.review ? (
        <Alert
          variant="warning"
          message="A change order is pending on this account. It must be resolved before this install package can be approved."
        />
      ) : shouldDisableReviewAsApproval && isFinCoUser && action === ReviewPackageActions.review ? (
        <Alert
          variant="warning"
          message="There are pending queue reviews or actions that need to be completed prior to approving this package."
        />
      ) : null}
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        alignItems={{
          base: 'stretch',
          tablet: 'flex-end',
        }}
        justifyContent={{
          tablet: 'flex-end',
        }}
        childGap="sm"
        style={{ flexShrink: 0 }}
        padding={{ desktop: 'lg 0 0 0', base: 'lg lg' }}
      >
        {flagCount > 0 && showFlagsCountToEPC && (
          <Box direction="row" fontSize="sm" padding="sm" gap="2xs" radius="sm" alignItems="center">
            <Icon name="flag" color="danger" />
            <Box color="danger">{`${flagCount} ${flagCount > 1 ? 'flags' : 'flag'}`}</Box>
          </Box>
        )}
        <Button
          as="a"
          navigate={() => navigate(`/accounts/${id ?? ''}`)}
          variant="secondary"
          tone="neutral"
          size="md"
          isLoading={isSubmitting || isLoading}
        >
          Cancel
        </Button>
        <Button
          size="md"
          variant="secondary"
          tone="primary"
          isLoading={isSubmitting || isLoading || !allowsAction}
          onClick={() => handleSave()}
          isDisabled={disabled}
        >
          Save
        </Button>
        {action === ReviewPackageActions.review ? (
          <CompleteQueueReview
            canSubmitReview={canSubmitReview}
            disableApproval={flagDrivenPackageReview ? shouldDisableReviewAsApproval : disableApproval}
            disableAllApprovals={disableAllApprovals}
            handleFormSave={pureSave}
            conditionallyApprovedFlags={conditionallyApprovedFlags}
            subQueueInfo={{ type: subQueueInfo?.type, status: subQueueInfo?.status }}
          />
        ) : (
          <Button
            size="md"
            variant="primary"
            type="submit"
            isLoading={isSubmitting || isLoading}
            isDisabled={isSubmitDisabled}
          >
            {isFirstSubmission ? 'Submit' : 'Resubmit'} for Review
          </Button>
        )}
      </Box>
    </Box>
  );
};
